/**
 * Mover plata de una caja a otra.
 *
 * POR QUE ESTE DIALOGO NO ES UN FORMULARIO DE TRANSFERENCIA COMUN
 * --------------------------------------------------------------
 * Si las dos cajas tienen la misma moneda, mover plata es mover plata: sale mil
 * de un lado y entran mil del otro.
 *
 * Si las monedas difieren NO es una transferencia, es una compra o una venta de
 * dolares. Ahi hace falta la cotizacion REAL de la operacion, que casi nunca es
 * la del sistema: el sistema tiene una referencia, y el cambio se pacto con
 * alguien en particular a un numero en particular. La diferencia entre las dos
 * es la ganancia o la perdida del cambio, y por eso la pantalla la muestra.
 *
 * Y sobre todo: cuanto ENTRA del otro lado se ve antes de confirmar, no despues.
 * Es el unico numero que la persona no calculo en la cabeza.
 */
import { useEffect, useMemo, useState } from "react";
import { ArrowLeft, ArrowRight } from "lucide-react";
import { formatear, parsearImporte } from "@tora/shared";

import { ChipMoneda, Importe, InputImporte } from "@/components/money";
import { Aviso } from "@/components/ui/aviso";
import { Button } from "@/components/ui/button";
import { Campo } from "@/components/ui/campo";
import { Card } from "@/components/ui/card";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { cn } from "@/lib/utils";
import type { CuentaFinanciera, NuevaTransferencia } from "@/lib/tipos";

import { Fila } from "./DialogMovimiento";
import { calcularCambio, textoCotizacion } from "./logica";

export interface DialogTransferenciaProps {
  abierto: boolean;
  onAbiertoChange: (abierto: boolean) => void;
  cuentas: ReadonlyArray<CuentaFinanciera>;
  /**
   * Que se esta haciendo. Son dos operaciones distintas y no una con variantes.
   *
   * "mover" es plata que cambia de bolsillo sin cambiar de moneda: no hay
   * cotizacion, no hay brecha, no genera resultado. "cambiar" es comprar o
   * vender divisa: hay una cotizacion pactada, y la diferencia contra la del
   * sistema es una ganancia o una perdida financiera de verdad.
   *
   * Estaban juntas en un solo boton que adivinaba segun las monedas elegidas.
   * El resultado era que se entraba a "transferir" y se terminaba cambiando
   * divisa sin haberlo decidido.
   */
  modo?: "mover" | "cambiar";
  /** La cotizacion que tiene cargada el sistema. Sirve de referencia y de default. */
  cotizacionSistema: number;
  /**
   * Los saldos de las cajas (lo que devuelve `/cajas`), para avisar en la
   * revision si lo que sale deja el origen en negativo. Opcional: sin saldos
   * no se avisa. Antes no se pasaban: sacar US$ 10.000 de un cajon con
   * US$ 6.987 lo dejaba en -US$ 3.012 sin que la revision dijera nada.
   */
  saldos?: ReadonlyArray<{ id: number; saldo: number | string }>;
  guardando?: boolean;
  onConfirmar: (datos: NuevaTransferencia) => void;
}

export function DialogTransferencia({
  abierto,
  onAbiertoChange,
  modo = "cambiar",
  cuentas,
  cotizacionSistema,
  saldos = [],
  guardando = false,
  onConfirmar,
}: DialogTransferenciaProps): JSX.Element {
  const [paso, setPaso] = useState<"carga" | "revision">("carga");
  const [origenId, setOrigenId] = useState<string>("");
  const [destinoId, setDestinoId] = useState<string>("");
  const [importe, setImporte] = useState<number | null>(null);
  const [cotizacionTipeada, setCotizacionTipeada] = useState<string>("");
  const [pactadoRedondo, setPactadoRedondo] = useState(false);
  const [importeDestino, setImporteDestino] = useState<number | null>(null);
  const [motivo, setMotivo] = useState("");
  /**
   * Cuántas veces se abrió el cuadro. Va como `key` de los campos de importe
   * para que cada apertura los cree de nuevo: el cuadro queda montado entre
   * Transferir y Comprar dólares, el campo arrancaba con el importe anterior,
   * tomaba el foco y ya no aceptaba el vacío del reinicio. Se veía «50.000,00»
   * vacío por dentro, y lo que se escribía quedaba pegado.
   */
  const [apertura, setApertura] = useState(0);

  useEffect(() => {
    if (!abierto) return;
    setApertura((n) => n + 1);
    setPaso("carga");
    setImporte(null);
    setImporteDestino(null);
    setPactadoRedondo(false);
    setMotivo("");
    setCotizacionTipeada("");
    // Depende SOLO de `abierto`: con `cuentas` o `cotizacionSistema` en la
    // lista, cualquier refresco de saldos o de cotizacion borraba lo cargado a
    // mitad de camino.
  }, [abierto]);

  // Lo que depende de datos que pueden llegar tarde se completa aparte y solo
  // si esta vacio: nunca pisa lo que la persona ya eligio o tipeo.
  useEffect(() => {
    if (!abierto) return;
    setOrigenId((prev) => prev || String(cuentas[0]?.id ?? ""));
    setDestinoId((prev) => prev || String(cuentas[1]?.id ?? ""));
  }, [abierto, cuentas]);

  useEffect(() => {
    if (!abierto || !(cotizacionSistema > 0)) return;
    setCotizacionTipeada((prev) => prev || textoCotizacion(cotizacionSistema));
  }, [abierto, cotizacionSistema]);

  const origen = useMemo(() => cuentas.find((c) => String(c.id) === origenId), [cuentas, origenId]);

  /**
   * En modo "mover", el destino solo puede ser una caja de la MISMA moneda.
   * Filtrar y no solo avisar: es la unica forma de que "transferir" no termine
   * siendo un cambio de divisa hecho sin querer.
   *
   * Y sin el origen: con una sola cuenta en dolares, la lista de destino solo
   * tenia al propio origen, el destino quedaba vacio y el dialogo de «misma
   * moneda» mostraba el recuadro de cotizacion.
   */
  const destinos = useMemo(
    () =>
      modo === "mover" && origen
        ? cuentas.filter((c) => c.moneda === origen.moneda && c.id !== origen.id)
        : modo === "cambiar" && origen
          ? cuentas.filter((c) => c.moneda !== origen.moneda)
          : cuentas,
    [cuentas, modo, origen],
  );

  const destino = useMemo(
    () => cuentas.find((c) => String(c.id) === destinoId),
    [cuentas, destinoId],
  );

  // Si el destino elegido dejo de ser valido al cambiar el origen, se corrige
  // solo. Dejarlo apuntando a una caja que ya no esta en la lista mostraba un
  // desplegable vacio y un boton que no hacia nada.
  useEffect(() => {
    if (!abierto) return;
    // Mientras el origen todavia no se fijo, este efecto no tiene nada que
    // corregir: corriendo antes del inicializador dejaba origen y destino
    // apuntando a la misma caja, y el boton de revisar no avanzaba nunca.
    if (!origenId) return;
    // Con el destino vacio tambien se completa: si el origen anterior no
    // tenia otra cuenta de su moneda, el destino quedaba en blanco y al
    // cambiar a un origen que si tiene no se volvia a elegir ninguno.
    if (destinoId && destinos.some((c) => String(c.id) === destinoId)) return;

    const otro = destinos.find((c) => String(c.id) !== origenId);
    setDestinoId(otro ? String(otro.id) : "");
  }, [abierto, destinos, destinoId, origenId]);

  const cotizacion = parsearImporte(cotizacionTipeada);

  // Sin destino se supone la moneda del origen: con «ARS» fijo, un origen en
  // dolares sin destino se leia como cambio de divisa y aparecia la cotizacion.
  const cambio = calcularCambio({
    monedaOrigen: origen?.moneda ?? "ARS",
    monedaDestino: destino?.moneda ?? origen?.moneda ?? "ARS",
    importeOrigen: importe,
    cotizacion,
    importeDestinoManual: pactadoRedondo ? importeDestino : null,
    cotizacionSistema,
  });

  const mismaCuenta = Boolean(origen && destino && origen.id === destino.id);
  const listo = Boolean(origen && destino) && !mismaCuenta && cambio.importeDestino !== null;
  const sinDestino = Boolean(origen) && destinos.length === 0;

  // Lo que sale y deja el origen en rojo se avisa en la revision, sin bloquear,
  // igual que un egreso en `DialogMovimiento`: el saldo puede estar mal
  // cargado y el movimiento haber pasado igual. Un cajon fisico en negativo
  // casi siempre es un error de carga, y antes se guardaba callado.
  const filaSaldo = origen ? saldos.find((s) => s.id === origen.id) : undefined;
  const saldoAntes = filaSaldo ? Number(filaSaldo.saldo) : null;
  const quedaNegativo =
    saldoAntes !== null &&
    Number.isFinite(saldoAntes) &&
    importe !== null &&
    saldoAntes - importe < 0;

  function confirmar(): void {
    if (!origen || !destino || importe === null) return;
    onConfirmar({
      origen_id: origen.id,
      destino_id: destino.id,
      importe,
      ...(cambio.esCambioDivisa && cambio.cotizacionEfectiva
        ? { cotizacion: cambio.cotizacionEfectiva }
        : {}),
      ...(cambio.esCambioDivisa && pactadoRedondo && importeDestino
        ? { importe_destino: importeDestino }
        : {}),
      ...(motivo.trim() ? { motivo: motivo.trim() } : {}),
    });
  }

  return (
    <Dialog open={abierto} onOpenChange={onAbiertoChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>
            {paso === "revision"
              ? "Revisá antes de mover la plata"
              : modo === "cambiar"
                ? "Comprar o vender dólares"
                : "Transferir entre cuentas"}
          </DialogTitle>
          <DialogDescription>
            {modo === "cambiar"
              ? "Pesos por dólares o al revés. La diferencia contra la cotización del sistema queda registrada como resultado financiero."
              : "Sale de una caja y entra en la otra, en la misma moneda. El resultado del negocio no cambia."}
          </DialogDescription>
        </DialogHeader>

        {paso === "carga" ? (
          <div className="space-y-4">
            <div className="grid gap-4 sm:grid-cols-2">
              <div className="space-y-1.5">
                <Label htmlFor="tr-origen">Sale de</Label>
                <Select value={origenId} onValueChange={setOrigenId}>
                  <SelectTrigger id="tr-origen" aria-label="Cuenta de origen">
                    <SelectValue placeholder="Origen" />
                  </SelectTrigger>
                  <SelectContent>
                    {cuentas.map((c) => (
                      <SelectItem key={c.id} value={String(c.id)}>
                        {c.nombre} · {c.moneda}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-1.5">
                <Label htmlFor="tr-destino">Entra en</Label>
                <Select value={destinoId} onValueChange={setDestinoId}>
                  <SelectTrigger id="tr-destino" aria-label="Cuenta de destino">
                    <SelectValue placeholder="Destino" />
                  </SelectTrigger>
                  <SelectContent>
                    {destinos.map((c) => (
                      <SelectItem
                        key={c.id}
                        value={String(c.id)}
                        disabled={String(c.id) === origenId}
                      >
                        {c.nombre} · {c.moneda}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>

            {sinDestino ? (
              <Aviso tono="warning" compacto>
                {modo === "mover"
                  ? `No hay otra cuenta en ${origen?.moneda === "USD" ? "dólares" : "pesos"}: para pasar a ${origen?.moneda === "USD" ? "pesos" : "dólares"} usá Comprar o vender dólares.`
                  : `No hay ninguna cuenta en ${origen?.moneda === "USD" ? "pesos" : "dólares"} para recibir el cambio.`}
              </Aviso>
            ) : null}

            {mismaCuenta ? (
              <Aviso tono="destructive" compacto>
                El origen y el destino son la misma caja.
              </Aviso>
            ) : null}

            <InputImporte
              key={`sale-${apertura}`}
              label={`Importe que sale${origen ? ` de ${origen.nombre}` : ""}`}
              valor={importe}
              onValorChange={setImporte}
              moneda={origen?.moneda ?? "ARS"}
              requerido
              autoFocus
            />

            {cambio.esCambioDivisa ? (
              <Card padding="normal" className="space-y-4">
                <div className="space-y-1.5">
                  <Label htmlFor="tr-cotizacion">Cotización de esta operación</Label>
                  <div className="flex items-center gap-2">
                    <span className="shrink-0 text-xs text-muted-foreground">US$ 1 =</span>
                    <Input
                      id="tr-cotizacion"
                      type="text"
                      inputMode="decimal"
                      autoComplete="off"
                      disabled={pactadoRedondo}
                      value={
                        // Con el destino cargado a mano la cotizacion deja de ser
                        // un dato y pasa a ser una consecuencia: se muestra la que
                        // sale de los dos importes, no la que estaba tipeada.
                        pactadoRedondo
                          ? cambio.cotizacionEfectiva
                            ? textoCotizacion(cambio.cotizacionEfectiva)
                            : ""
                          : cotizacionTipeada
                      }
                      onChange={(e) => setCotizacionTipeada(e.target.value)}
                      className="text-right font-medium tabular-nums"
                    />
                    <ChipMoneda moneda="ARS" />
                  </div>
                  <p className="text-xs text-muted-foreground">
                    {pactadoRedondo
                      ? "Sale de los dos importes que cargaste."
                      : `Arranca en la del sistema (${textoCotizacion(cotizacionSistema)}), pero la que vale es la que pactaste vos.`}
                  </p>
                </div>

                <Checkbox
                  checked={pactadoRedondo}
                  onChange={(e) => {
                    setPactadoRedondo(e.target.checked);
                    if (!e.target.checked) setImporteDestino(null);
                  }}
                  label="El cambio se cerró en un número redondo. Cargo cuánto entra y la cotización sale de ahí."
                />

                {pactadoRedondo ? (
                  <InputImporte
                    key={`entra-${apertura}`}
                    label={`Importe que entra${destino ? ` en ${destino.nombre}` : ""}`}
                    valor={importeDestino}
                    onValorChange={setImporteDestino}
                    moneda={destino?.moneda ?? "USD"}
                    requerido
                  />
                ) : null}
              </Card>
            ) : null}

            <Campo label="Motivo">
              <Input
                id="tr-motivo"
                value={motivo}
                onChange={(e) => setMotivo(e.target.value)}
                placeholder="Por que se mueve esta plata"
                autoComplete="off"
              />
            </Campo>

            <VistaPreviaCambio
              origen={origen?.nombre}
              destino={destino?.nombre}
              monedaOrigen={origen?.moneda ?? "ARS"}
              monedaDestino={destino?.moneda ?? "ARS"}
              importeOrigen={importe}
              cambio={cambio}
              cotizacionSistema={cotizacionSistema}
            />
          </div>
        ) : (
          <Card variant="muted" padding="normal" className="space-y-3">
            <dl className="space-y-2">
              <Fila
                termino="Sale"
                valor={
                  <span className="inline-flex flex-wrap items-baseline justify-end gap-1">
                    <Importe monto={importe ?? 0} moneda={origen?.moneda ?? "ARS"} />
                    <span>· {origen?.nombre}</span>
                  </span>
                }
              />
              <Fila
                termino="Entra"
                valor={
                  <span className="inline-flex flex-wrap items-baseline justify-end gap-1">
                    <Importe monto={cambio.importeDestino ?? 0} moneda={destino?.moneda ?? "ARS"} />
                    <span>· {destino?.nombre}</span>
                  </span>
                }
              />
              {cambio.esCambioDivisa && cambio.cotizacionEfectiva ? (
                <Fila
                  termino="Cotización"
                  valor={
                    <span className="tabular-nums">
                      US$ 1 = $ {textoCotizacion(cambio.cotizacionEfectiva)}
                    </span>
                  }
                />
              ) : null}
              {motivo.trim() ? <Fila termino="Motivo" valor={motivo.trim()} /> : null}
            </dl>

            {quedaNegativo && origen && saldoAntes !== null && importe !== null ? (
              <Aviso tono="warning" compacto>
                {origen.nombre} tiene {formatear(saldoAntes, origen.moneda)} y queda en{" "}
                {formatear(saldoAntes - importe, origen.moneda)}. Revisá que sea la caja correcta y
                el importe; si lo es, se puede mover igual.
              </Aviso>
            ) : null}

            {cambio.esCambioDivisa ? (
              <p className="text-xs text-muted-foreground">
                Esto no cambia el resultado del negocio por sí solo: es plata que cambia de bolsillo
                y de moneda. Lo que sí queda registrado es la diferencia contra la cotización del
                sistema.
              </p>
            ) : (
              <p className="text-xs text-muted-foreground">
                Una transferencia entre cajas propias no es ni ingreso ni egreso: no entra al
                resultado.
              </p>
            )}
          </Card>
        )}

        <DialogFooter>
          {paso === "carga" ? (
            <>
              <Button type="button" variant="ghost" onClick={() => onAbiertoChange(false)}>
                Cancelar
              </Button>
              <Button type="button" disabled={!listo} onClick={() => setPaso("revision")}>
                Revisar
              </Button>
            </>
          ) : (
            <>
              <Button type="button" variant="ghost" onClick={() => setPaso("carga")}>
                <ArrowLeft aria-hidden="true" />
                Volver
              </Button>
              <Button type="button" cargando={guardando} onClick={confirmar}>
                Confirmar y mover
              </Button>
            </>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

/**
 * Cuanto entra del otro lado, mientras se tipea.
 *
 * Es el corazon de la pantalla: nadie puede confirmar un cambio de divisa sin
 * ver este numero, porque es justamente el que no tiene en la cabeza.
 */
function VistaPreviaCambio({
  origen,
  destino,
  monedaOrigen,
  monedaDestino,
  importeOrigen,
  cambio,
  cotizacionSistema,
}: {
  origen?: string;
  destino?: string;
  monedaOrigen: "ARS" | "USD";
  monedaDestino: "ARS" | "USD";
  importeOrigen: number | null;
  cambio: ReturnType<typeof calcularCambio>;
  cotizacionSistema: number;
}): JSX.Element {
  if (cambio.problema !== null) {
    return (
      <p data-testid="preview-cambio" className="text-xs text-muted-foreground">
        {cambio.problema}
      </p>
    );
  }

  const ganancia = cambio.diferenciaArs ?? 0;

  return (
    <Card variant="muted" padding="normal" className="space-y-2" data-testid="preview-cambio">
      <div className="flex flex-wrap items-center gap-2">
        <Importe monto={importeOrigen ?? 0} moneda={monedaOrigen} />
        <ArrowRight className="size-4 shrink-0 text-muted-foreground" aria-hidden="true" />
        <span data-testid="importe-destino">
          <Importe
            monto={cambio.importeDestino ?? 0}
            moneda={monedaDestino}
            tamano="lg"
            className="font-semibold"
          />
        </span>
      </div>

      <p className="flex flex-wrap items-center gap-1 text-xs text-muted-foreground">
        <span>{origen ?? "origen"}</span>
        <ArrowRight className="size-3 shrink-0" aria-hidden="true" />
        <span>{destino ?? "destino"}</span>
        {cambio.esCambioDivisa && cambio.cotizacionEfectiva ? (
          <span>· a US$ 1 = $ {textoCotizacion(cambio.cotizacionEfectiva)}</span>
        ) : null}
      </p>

      {cambio.esCambioDivisa && cambio.diferenciaArs !== null && cotizacionSistema > 0 ? (
        <p className={cn("text-xs", ganancia < 0 ? "text-warning" : "text-muted-foreground")}>
          Contra la cotización del sistema ($ {textoCotizacion(cotizacionSistema)}), este cambio{" "}
          {ganancia < 0 ? "pierde" : "deja"}{" "}
          <Importe monto={Math.abs(ganancia)} moneda="ARS" tamano="sm" codigo={false} />.
        </p>
      ) : null}
    </Card>
  );
}
